import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DashboardBarChart from '../../components/charts/DashboardBarChart';
import ChartDetailModal from '../../components/Dashboard/ChartDetailModal';
import BandwidthCycleTimeService from '../../services/BandwidthCycleTimeService';

const ALLIANZ_BLUE = '#003781';

// Same 3-series blue family already used for New/WIP/Completed elsewhere in
// this dashboard (Graph 1a/1b) -- reused here rather than picking new colors,
// so "3 categorical series" reads the same way across every tab.
//
// Order is left-to-right current -> automated -> achieved, so each group reads
// as "what it used to cost, what we committed to, what it costs now". Shade
// follows that order lightest-to-darkest: the two SLA figures are references,
// the measured value is the point of the chart.
const PHASE_SERIES = [
  { dataKey: 'CurrentSLA',   name: 'Phased SLA current (manual)',   color: '#1976D2' },
  { dataKey: 'AutomatedSLA', name: 'Phased SLA automated (target)', color: '#0057A8' },
  { dataKey: 'Achieved',     name: 'Achieved (measured, median)',   color: '#003781' },
];

// The per-stage chart carries no SLA figures: the client's tracker commits to
// numbers per PHASE, not per stage, and our stage boundaries straddle its phase
// boundaries (see server/config/stage_targets.py). Target/Manual are declared
// here anyway so that if those per-stage dicts are ever populated the bars
// appear on their own -- visibleSeries below drops any series that is entirely
// null, so an all-null series never contributes a phantom legend entry.
const STAGE_SERIES = [
  { dataKey: 'Target',   name: 'Target SLA (agreed)',          color: '#1976D2' },
  { dataKey: 'Manual',   name: 'Manual (pre-automation)',      color: '#0057A8' },
  { dataKey: 'Achieved', name: 'Automated (measured, median)', color: '#003781' },
];

const VARIANT_OPTIONS = [
  { key: 'soft',         label: 'Soft Bandwidth Upgrade' },
  { key: 'hard',         label: 'Hard Bandwidth Upgrade' },
  { key: 'non_standard', label: 'Non-standard Request' },
  { key: 'all',          label: 'All variants (no SLA)' },
];

const CONFIDENCE_LABEL = { none: 'None', low: 'Low', medium: 'Medium', high: 'High' };

/**
 * Minimum bar height, per data point.
 *
 * The function form, not a bare number. Recharts applies the minimum height
 * BEFORE it discards zero-height rectangles, so a bare `minPointSize={3}`
 * inflates a genuine 0 into a visible sliver -- which is how the first version
 * of this chart ended up a row of identical stubs sitting on the axis. Zero and
 * "no value at all" both get no sliver here; only a real, non-zero,
 * too-small-to-see duration does.
 */
const barMinPointSize = (value) =>
  value === null || value === undefined || value === 0 ? 0 : 3;

/**
 * Round a positive number UP to a "nice" value (a round multiple of a power of
 * ten). Applied ourselves rather than left to Recharts because these charts mix
 * a multi-day SLA with a several-second measured value on one axis --
 * Recharts' auto-scale has no reason to expect that, and letting it guess
 * produced a scale that didn't fit the data at all.
 *
 * The ladder is finer than the usual 1/2/5: the real SLA figures are business
 * day counts like 26 and 28, and a 1/2/5 ladder rounds 28 up to 50, throwing
 * away nearly half the plot width for nothing.
 */
const NICE_STEPS = [1, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10];

function niceCeil(value) {
  if (!(value > 0)) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
  const normalized = value / magnitude;
  const step = NICE_STEPS.find((s) => normalized <= s + 1e-9) || 10;
  return step * magnitude;
}

/**
 * Axis units, coarsest first. The whole axis plots in ONE unit -- a mixed-unit
 * axis can't be compared by bar height, which is the only thing a bar chart is
 * for. This picks the coarsest unit that still leaves the largest bar as a
 * readable number, so the per-stage chart reads in seconds today while the
 * phase chart reads in days (its SLA bars are tens of business days).
 */
const AXIS_UNITS = [
  { key: 'days',    perDay: 1,     suffix: 'd', minDays: 1 },
  { key: 'hours',   perDay: 24,    suffix: 'h', minDays: 1 / 24 },
  { key: 'minutes', perDay: 1440,  suffix: 'm', minDays: 1 / 1440 },
  { key: 'seconds', perDay: 86400, suffix: 's', minDays: 0 },
];

/**
 * The measured median as a PRECISE value in days.
 *
 * Not `median_actual_days` -- the endpoint rounds that to one decimal place,
 * which is a resolution of 2.4 hours, so anything finishing faster than that
 * arrives as exactly 0.0 and plots as nothing no matter how the axis is scaled.
 * `median_actual_minutes` is derived from the same unrounded figure at
 * 0.01-minute (0.6-second) resolution, so days are rebuilt from it and the
 * precision survives. Falls back to the rounded field only if minutes is absent.
 */
function preciseMedianDays(row) {
  const minutes = row.median_actual_minutes;
  if (minutes !== null && minutes !== undefined) return minutes / 1440;
  const days = row.median_actual_days;
  return days === undefined ? null : days;
}

/**
 * Per-bar label text from a precise day value. Unlike the axis, each label
 * picks its own unit, so a two-second stage sitting next to a 47-minute one is
 * legible in both places instead of both reading "0.0".
 */
function formatDuration(days) {
  if (days === null || days === undefined || Number.isNaN(days)) return '';
  // An exact 0 is a committed target of zero days (Soft/Hard automated
  // Solutioning), not something that was measured at sub-second resolution --
  // labelling it "0.0s" would read as a measurement. Distinct from '' above,
  // which is the no-value case.
  if (days === 0) return '0';
  const seconds = days * 86400;
  if (seconds < 60) return `${seconds.toFixed(seconds < 10 ? 1 : 0)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  if (seconds < 86400) return `${(seconds / 3600).toFixed(1)}h`;
  return `${days.toFixed(1)}d`;
}

/**
 * Axis unit + domain + formatters for a set of day-valued rows.
 *
 * Shared by both charts on this tab so they scale by identical rules while
 * keeping independent axes -- the phase chart's SLA bars are tens of days and
 * would flatten the per-stage chart's seconds-long bars into nothing if the two
 * were forced onto one scale.
 */
function buildScale(dayRows, dataKeys) {
  const values = dayRows
    .flatMap((row) => dataKeys.map((k) => row[k]))
    .filter((v) => v !== null && v !== undefined && !Number.isNaN(v) && v > 0);
  const maxDays = values.length ? Math.max(...values) : 0;
  const unit = AXIS_UNITS.find((u) => maxDays >= u.minDays) || AXIS_UNITS[AXIS_UNITS.length - 1];
  return {
    unit,
    // 5% headroom above the tallest bar. Without it a bar whose value lands
    // exactly on the nice ceiling reaches the top of the plot and its value
    // label is clipped by the frame -- which is what happened to "Intake to
    // Quote Received".
    domain: [0, niceCeil(maxDays * unit.perDay * 1.05)],
    // Number(toFixed(2)) trims the float noise an interpolated tick can carry
    // (16.666666666666668s) without hard-coding a decimal count.
    tickFormatter: (v) =>
      typeof v === 'number' ? `${Number(v.toFixed(2))}${unit.suffix}` : `${v}`,
    // LabelList hands back the PLOTTED number (axis units) -- convert back to
    // days so each label can choose its own unit.
    labelFormatter: (v) =>
      v === null || v === undefined ? '' : formatDuration(v / unit.perDay),
    toUnit: (v) => (v === null || v === undefined ? null : v * unit.perDay),
  };
}

/**
 * Drop any series with no data at all, so an entirely-null series cannot put a
 * colour in the legend that appears nowhere in the plot.
 */
function visibleSeries(series, rows) {
  return series.filter((s) =>
    rows.some((row) => row[s.dataKey] !== null && row[s.dataKey] !== undefined)
  );
}

/** "1 request" vs "3 requests" — the chip labels below hard-coded the plural. */
function pluralize(count, singular, plural = `${singular}s`) {
  return count === 1 ? singular : plural;
}

/** Reusable styled section header with left accent bar (matches AnalyticsDashboardContent) */
const SectionHeader = ({ title }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1.5 }}>
    <Box sx={{ width: 4, height: 24, borderRadius: 2, bgcolor: ALLIANZ_BLUE }} />
    <Typography variant="h6" fontWeight={700} sx={{ color: '#1a1a2e' }}>
      {title}
    </Typography>
  </Box>
);

const chartPaperSx = {
  borderRadius: '16px',
  border: '1px solid #d6e4f7',
  boxShadow: '0 2px 16px rgba(0,55,129,0.07)',
  p: 2.5,
  background: '#f5f8fd',
};

const StageCycleTimeContent = () => {
  const currentYear = String(new Date().getFullYear());
  const [year, setYear] = useState('');
  const [variant, setVariant] = useState('soft');
  // '' = all tickets, 'closed' | 'open' -- orthogonal to variant: a request is
  // scored against its variant's SLA table regardless of whether it's done yet.
  const [status, setStatus] = useState('');
  // Selected demand-number OPTIONS (full {request_id, grm_demand_number, title,
  // request_state} objects, not bare strings) -- several real UAT rows share
  // ordinary-looking placeholder demand numbers, so the picker needs the title
  // to tell them apart, and the request sent to the backend only needs the
  // grm_demand_number string back out of whichever objects got picked.
  const [selectedDemandNumbers, setSelectedDemandNumbers] = useState([]);
  const [demandNumberOptions, setDemandNumberOptions] = useState([]);
  const [availableYears, setAvailableYears] = useState([currentYear]);
  const [phaseResponse, setPhaseResponse] = useState(null);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [phaseModalOpen, setPhaseModalOpen] = useState(false);
  const [stageModalOpen, setStageModalOpen] = useState(false);
  const [stageDetailRows, setStageDetailRows] = useState([]);

  useEffect(() => {
    BandwidthCycleTimeService.getFilterOptions()
      .then((opts) => setAvailableYears(opts.years && opts.years.length > 0 ? opts.years : [currentYear]))
      .catch((err) => console.error('Failed to load filter options', err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bare grm_demand_number strings out of the selected option objects -- the
  // objects carry title/state for the picker's own display, but the backend
  // filter only wants the code itself.
  const demandNumberFilter = useMemo(
    () => selectedDemandNumbers.map((opt) => opt.grm_demand_number),
    [selectedDemandNumbers]
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Both charts describe the same requests, so they are fetched together --
      // a partial refresh would leave the phase totals and the stage breakdown
      // describing different snapshots.
      const [phaseData, stageData] = await Promise.all([
        BandwidthCycleTimeService.getPhaseCycleTime({
          year, variant, status, grm_demand_number: demandNumberFilter,
        }),
        BandwidthCycleTimeService.getStageCycleTime({
          year, status, grm_demand_number: demandNumberFilter,
        }),
      ]);
      setPhaseResponse(phaseData);
      setStages(stageData);
    } catch (err) {
      console.error('Cycle time fetch failed', err);
      setError('Failed to load cycle time data.');
    } finally {
      setLoading(false);
    }
  }, [year, variant, status, demandNumberFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // GRM Number picker's own options -- every bandwidth request with a demand
  // number, scoped by year/status like everything else on this page (a
  // number that only appears in a different year would be a confusing pick).
  useEffect(() => {
    BandwidthCycleTimeService.getBandwidthDemandNumbers({ year, status })
      .then(setDemandNumberOptions)
      .catch((err) => {
        console.error('Failed to load demand-number options', err);
        setDemandNumberOptions([]);
      });
  }, [year, status]);

  // Per-request detail (demand number + start/end/days per stage) is only
  // needed for the Stage modal's export sheet -- fetched lazily on open, not
  // alongside the chart data every load, since it is a much wider payload
  // (one row per bandwidth request, 3 columns per stage) that most visits to
  // this tab never need. Filtered by the SAME Request type / Ticket status /
  // GRM Number filters that drive the charts -- a demand-number list mixing
  // everything together is not what "filtered to this selection" means to
  // whoever reads it.
  useEffect(() => {
    if (!stageModalOpen) return;
    BandwidthCycleTimeService.getStageCycleTimeDetail({
      year, variant, status, grm_demand_number: demandNumberFilter,
    })
      .then(setStageDetailRows)
      .catch((err) => {
        console.error('Stage cycle time detail fetch failed', err);
        setStageDetailRows([]);
      });
  }, [stageModalOpen, year, variant, status, demandNumberFilter]);

  const phases = phaseResponse?.phases || [];
  const variantCounts = phaseResponse?.variant_counts || {};
  const unclassifiedCount = variantCounts.unclassified || 0;
  const overlapCount = phaseResponse?.non_standard_hard_count || 0;

  // ---- phase chart ----------------------------------------------------------
  // Everything is held in days first -- both SLA figures are natively days, and
  // the measured value is rebuilt into days at full precision. null stays null:
  // a phase with no client-supplied figure is a genuine gap (Soft and Hard
  // "automated Solutioning" is one), and must never be drawn as a 0-length bar.
  const phaseDayRows = useMemo(
    () =>
      phases.map((p) => ({
        phase_label: p.phase_label,
        CurrentSLA: p.sla_current_days ?? null,
        AutomatedSLA: p.sla_automated_days ?? null,
        Achieved: preciseMedianDays(p),
      })),
    [phases]
  );

  const phaseScale = useMemo(
    () => buildScale(phaseDayRows, ['CurrentSLA', 'AutomatedSLA', 'Achieved']),
    [phaseDayRows]
  );

  const phaseChartData = useMemo(
    () =>
      phaseDayRows.map((row) => ({
        phase_label: row.phase_label,
        CurrentSLA: phaseScale.toUnit(row.CurrentSLA),
        AutomatedSLA: phaseScale.toUnit(row.AutomatedSLA),
        Achieved: phaseScale.toUnit(row.Achieved),
      })),
    [phaseDayRows, phaseScale]
  );

  const phaseSeries = useMemo(
    () => visibleSeries(PHASE_SERIES, phaseChartData),
    [phaseChartData]
  );

  const phaseTableData = useMemo(
    () =>
      phases.map((p) => ({
        ...p,
        stage_coverage: `${p.stages_measured}/${p.stages_total}`,
        confidence: CONFIDENCE_LABEL[p.confidence] || p.confidence,
      })),
    [phases]
  );

  const phaseTableColumns = [
    { key: 'phase_label', label: 'Phase' },
    { key: 'sla_current_days', label: 'SLA current (d)' },
    { key: 'sla_automated_days', label: 'SLA automated (d)' },
    { key: 'median_actual_days', label: 'Achieved median (d)' },
    { key: 'median_actual_hours', label: 'Achieved median (h)' },
    { key: 'median_actual_minutes', label: 'Achieved median (min)' },
    { key: 'sum_of_stages_days', label: 'Sum of stages (d)' },
    { key: 'delta_days', label: 'Delta vs automated (d)' },
    { key: 'sample_size', label: 'Sample size' },
    { key: 'in_flight_count', label: 'In flight' },
    { key: 'stage_coverage', label: 'Stages measured' },
    { key: 'confidence', label: 'Confidence' },
  ];

  // ---- per-stage chart ------------------------------------------------------
  const stageDayRows = useMemo(
    () =>
      stages.map((s) => ({
        stage_label: s.stage_label,
        Manual: s.manual_days ?? null,
        Target: s.target_days ?? null,
        Achieved: preciseMedianDays(s),
      })),
    [stages]
  );

  const stageScale = useMemo(
    () => buildScale(stageDayRows, ['Manual', 'Target', 'Achieved']),
    [stageDayRows]
  );

  const stageChartData = useMemo(
    () =>
      stageDayRows.map((row) => ({
        stage_label: row.stage_label,
        Manual: stageScale.toUnit(row.Manual),
        Target: stageScale.toUnit(row.Target),
        Achieved: stageScale.toUnit(row.Achieved),
      })),
    [stageDayRows, stageScale]
  );

  const stageSeries = useMemo(
    () => visibleSeries(STAGE_SERIES, stageChartData),
    [stageChartData]
  );

  const stageTableData = useMemo(
    () =>
      stages.map((s) => ({
        ...s,
        confidence: CONFIDENCE_LABEL[s.confidence] || s.confidence,
      })),
    [stages]
  );

  const stageTableColumns = [
    { key: 'stage_label', label: 'Stage' },
    { key: 'mean_actual_days', label: 'Mean Actual (d)' },
    { key: 'median_actual_days', label: 'Median Actual (d)' },
    { key: 'median_actual_hours', label: 'Median Actual (h)' },
    { key: 'median_actual_minutes', label: 'Median Actual (min)' },
    { key: 'sample_size', label: 'Sample Size' },
    { key: 'in_flight_count', label: 'In Flight' },
    { key: 'confidence', label: 'Confidence' },
  ];

  const totalInFlight = stages.reduce((sum, s) => sum + (s.in_flight_count || 0), 0);
  const measuredStages = stages.filter((s) => s.sample_size > 0).length;

  const variantLabel = (opt) => {
    if (opt.key === 'all') return opt.label;
    const count = variantCounts[opt.key];
    return count === undefined ? opt.label : `${opt.label} (${count})`;
  };

  return (
    <Box p={3}>
      <SectionHeader title="Bandwidth SLA Tracker by Phase" />
      <Typography variant="body2" color="text.secondary" mb={2}>
        The SLA tracker commits to a target per <strong>phase</strong>, in three separate tables
        for Soft, Hard and Non-standard requests. Each phase below shows the tracker's pre-automation
        figure, its automated target, and what we actually measured — the elapsed time across all the
        stages inside that phase. Requests are assigned to a variant using the same two values the portal
        hands the workflow at intake, so each one is scored against the table its own workflow ran.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filter bar */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 3,
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          boxShadow: 'none',
          background: '#f0f6ff',
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
          <FormControl size="small" sx={{ minWidth: 230 }}>
            <InputLabel id="cycle-variant-label" shrink>Request type</InputLabel>
            <Select
              labelId="cycle-variant-label"
              value={variant}
              label="Request type"
              onChange={(e) => setVariant(e.target.value)}
            >
              {VARIANT_OPTIONS.map((opt) => (
                <MenuItem key={opt.key} value={opt.key}>{variantLabel(opt)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel id="stage-cycle-year-label" shrink>Year</InputLabel>
            <Select
              labelId="stage-cycle-year-label"
              value={year}
              label="Year"
              displayEmpty
              onChange={(e) => setYear(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              {availableYears.map((y) => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id="cycle-status-label" shrink>Ticket status</InputLabel>
            <Select
              labelId="cycle-status-label"
              value={status}
              label="Ticket status"
              displayEmpty
              onChange={(e) => setStatus(e.target.value)}
            >
              <MenuItem value=""><em>All</em></MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
              <MenuItem value="open">Open</MenuItem>
            </Select>
          </FormControl>
          <Autocomplete
            multiple
            size="small"
            sx={{ minWidth: 320 }}
            options={demandNumberOptions}
            value={selectedDemandNumbers}
            onChange={(_e, value) => setSelectedDemandNumbers(value)}
            getOptionLabel={(opt) => `${opt.grm_demand_number} — ${opt.title || 'untitled'}`}
            isOptionEqualToValue={(opt, val) => opt.request_id === val.request_id}
            renderInput={(params) => (
              <TextField {...params} label="GRM demand number" placeholder="Search demand number…" />
            )}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={fetchData} size="small" sx={{ color: ALLIANZ_BLUE }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          {!loading && (
            <Stack direction="row" spacing={1} flexWrap="wrap">
              <Chip
                size="small"
                label={`${phaseResponse?.requests_in_variant ?? 0} ${pluralize(phaseResponse?.requests_in_variant ?? 0, 'request')} in this type`}
                sx={{ bgcolor: '#eef2fb', color: '#374151', fontSize: 12 }}
              />
              <Chip
                size="small"
                label={`${measuredStages}/${stages.length} stages have data`}
                sx={{ bgcolor: '#eef2fb', color: '#374151', fontSize: 12 }}
              />
              {totalInFlight > 0 && (
                <Chip
                  size="small"
                  icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={`${totalInFlight} ${pluralize(totalInFlight, 'request')} currently in flight`}
                  sx={{ bgcolor: '#fff4e5', color: '#9a6700', fontSize: 12 }}
                />
              )}
              {unclassifiedCount > 0 && (
                <Tooltip title="These requests record no usable upgrade type, so they belong to no SLA table and are counted in no variant above. Left out rather than assigned a default, which would score them against an SLA they were never measured for.">
                  <Chip
                    size="small"
                    icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                    label={`${unclassifiedCount} unclassified`}
                    sx={{ bgcolor: '#fff4e5', color: '#9a6700', fontSize: 12 }}
                  />
                </Tooltip>
              )}
              {overlapCount > 0 && (variant === 'non_standard' || variant === 'all') && (
                <Tooltip title="Requests that are both hard and non-standard. The tracker has no table for that combination, so they are counted under Non-standard — whose Approval SLA is 3 business days lower than Hard's.">
                  <Chip
                    size="small"
                    icon={<InfoOutlinedIcon sx={{ fontSize: 14 }} />}
                    label={`${overlapCount} also hard`}
                    sx={{ bgcolor: '#fff4e5', color: '#9a6700', fontSize: 12 }}
                  />
                </Tooltip>
              )}
            </Stack>
          )}
        </Stack>
      </Paper>

      {/* Phase chart -- the primary view */}
      <Paper elevation={0} sx={chartPaperSx}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
            SLA current vs. SLA automated vs. achieved — per phase
            {phaseResponse?.variant_label ? ` (${phaseResponse.variant_label})` : ''}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setPhaseModalOpen(true)}
            sx={{ textTransform: 'none', fontSize: 12, color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE }}
          >
            Data View
          </Button>
        </Stack>
        <Divider sx={{ mt: 1.5, mb: 2, borderColor: '#f0f1f3' }} />
        <DashboardBarChart
          data={phaseChartData}
          xKey="phase_label"
          series={phaseSeries}
          height={460}
          loading={loading}
          xTickWrap
          yDomain={phaseScale.domain}
          yTickFormatter={phaseScale.tickFormatter}
          minPointSize={barMinPointSize}
          showValueLabels
          valueLabelFormatter={phaseScale.labelFormatter}
        />
        {!loading && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
            The axis is in {phaseScale.unit.key}; every bar is labelled with its own real duration. Where
            "Achieved" shows no bar for a phase, no request in this selection has completed it yet. A bare
            "0" with no bar marks a genuine zero-day target — the automated path skips that phase entirely
            for this variant, not a missing figure. Check the sample size and confidence in the Data View
            before reading a figure as a benchmark — a small sample, or one moving unusually fast through
            the workflow, is not yet representative.
            {variant === 'all' && ' Pooled across all variants, so no SLA figures are shown — the three tables disagree.'}
          </Typography>
        )}
      </Paper>

      {/* Per-stage chart -- the measured breakdown behind the phase totals */}
      <Box mt={3}>
        <Paper elevation={0} sx={chartPaperSx}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#374151' }}>
              Measured breakdown — per stage (all variants)
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setStageModalOpen(true)}
              sx={{ textTransform: 'none', fontSize: 12, color: ALLIANZ_BLUE, borderColor: ALLIANZ_BLUE }}
            >
              Data View
            </Button>
          </Stack>
          <Divider sx={{ mt: 1.5, mb: 2, borderColor: '#f0f1f3' }} />
          <DashboardBarChart
            data={stageChartData}
            xKey="stage_label"
            series={stageSeries}
            height={420}
            loading={loading}
            xTickWrap
            yDomain={stageScale.domain}
            yTickFormatter={stageScale.tickFormatter}
            minPointSize={barMinPointSize}
            showValueLabels
            valueLabelFormatter={stageScale.labelFormatter}
          />
          {!loading && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
              Measured time only, with no SLA bars: the tracker states its commitments per phase, and these
              stage boundaries cut across the tracker's phase boundaries, so a per-stage target would imply
              a commitment that was never made. Not filtered by request type — this is every request in
              the selected year, shown as the raw breakdown behind the phase totals above. Axis in{' '}
              {stageScale.unit.key}.
            </Typography>
          )}
        </Paper>
      </Box>

      <ChartDetailModal
        open={phaseModalOpen}
        onClose={() => setPhaseModalOpen(false)}
        title="Bandwidth SLA Tracker by Phase"
        subtitle={`Phased SLA current, phased SLA automated, and measured achieved time (median) per phase — ${phaseResponse?.variant_label || ''}`}
        accentColor={ALLIANZ_BLUE}
        chartType="bar"
        chartData={phaseChartData}
        xKey="phase_label"
        series={phaseSeries}
        tableData={phaseTableData}
        tableColumns={phaseTableColumns}
        rawData={phases}
        exportName="bandwidth-sla-tracker-by-phase"
        xTickWrap
        yDomain={phaseScale.domain}
        yTickFormatter={phaseScale.tickFormatter}
        minPointSize={barMinPointSize}
        showValueLabels
        valueLabelFormatter={phaseScale.labelFormatter}
      />

      <ChartDetailModal
        open={stageModalOpen}
        onClose={() => setStageModalOpen(false)}
        title="Bandwidth SLA Tracker — Stage Detail"
        subtitle="Measured automated time (median) per stage, across all request types"
        accentColor={ALLIANZ_BLUE}
        chartType="bar"
        chartData={stageChartData}
        xKey="stage_label"
        series={stageSeries}
        tableData={stageTableData}
        tableColumns={stageTableColumns}
        rawData={stages}
        exportName="bandwidth-sla-tracker-by-stage"
        extraSheets={[{ name: 'Per-Request Detail', rows: stageDetailRows }]}
        xTickWrap
        yDomain={stageScale.domain}
        yTickFormatter={stageScale.tickFormatter}
        minPointSize={barMinPointSize}
        showValueLabels
        valueLabelFormatter={stageScale.labelFormatter}
      />
    </Box>
  );
};

export default StageCycleTimeContent;
